/** Modulo that always returns a value in [0, n), also for negative inputs. */
export function mod(value: number, n: number): number {
  return ((value % n) + n) % n;
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** Median of the first `length` values of `values`. Reorders `values` in place. */
export function medianInPlace(values: Float64Array | number[], length = values.length): number {
  if (length === 0) return 0;
  // Insertion sort: the windows used here are small (≤ 17 values).
  for (let i = 1; i < length; i++) {
    const v = values[i] ?? 0;
    let j = i - 1;
    while (j >= 0 && (values[j] ?? 0) > v) {
      values[j + 1] = values[j] ?? 0;
      j--;
    }
    values[j + 1] = v;
  }
  const mid = length >> 1;
  return length % 2 ? (values[mid] ?? 0) : 0.5 * ((values[mid - 1] ?? 0) + (values[mid] ?? 0));
}

export function argmax(values: ArrayLike<number>): number {
  let best = 0;
  for (let i = 1; i < values.length; i++) if ((values[i] ?? 0) > (values[best] ?? 0)) best = i;
  return best;
}

export function l2norm(values: Iterable<number>): number {
  let sum = 0;
  for (const v of values) sum += v * v;
  return Math.sqrt(sum);
}

/** Pearson correlation of two equal-length vectors; 0 when either is constant. */
export function pearson(a: ArrayLike<number>, b: ArrayLike<number>): number {
  const n = a.length;
  let ma = 0;
  let mb = 0;
  for (let i = 0; i < n; i++) {
    ma += a[i] ?? 0;
    mb += b[i] ?? 0;
  }
  ma /= n;
  mb /= n;
  let num = 0;
  let da = 0;
  let db = 0;
  for (let i = 0; i < n; i++) {
    const x = (a[i] ?? 0) - ma;
    const y = (b[i] ?? 0) - mb;
    num += x * y;
    da += x * x;
    db += y * y;
  }
  const den = Math.sqrt(da * db);
  return den > 0 ? num / den : 0;
}

/** log(sum(exp(values))) without overflow. */
export function logSumExp(values: Iterable<number>): number {
  let max = -Infinity;
  for (const v of values) max = Math.max(max, v);
  if (max === -Infinity) return -Infinity;
  let sum = 0;
  for (const v of values) sum += Math.exp(v - max);
  return max + Math.log(sum);
}
